import {
  ActionIcon,
  Box,
  Button,
  Divider,
  Flex,
  Select,
  TagsInput,
  Text,
  TextInput,
} from '@cowbell/ui';
import {
  faBold,
  faItalic,
  faMinus,
  faPaperclip,
  faReply,
  faTrashCan,
  faUpRightAndDownLeftFromCenter,
  faWandMagicSparkles,
  faXmark,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import useNotifications from '../../../_lib/notifications/useNotifications';
import { sendEmail } from '../../../api/cmail.api';
import type {
  MessageInfo,
  MessageInfoReceiver,
} from '../../../api/cmail/cmail.dto';
import {
  getSignaturesByEmail,
  type SignaturesByEmailResponse,
} from '../../../api/signature-assignment.api';
import { signaturesApi } from '../../../api/signatures.api';
import { useAllow } from '../../../components/hooks/useAllow';
import { useAPIErrorHandler } from '../../../components/hooks/useAPIErrorHandler';
import { useSystemSignatures } from '../../../hooks/useSystemSignatures';
import { queries } from '../../../queries';
import {
  useAIAssist,
  useAttachmentManagement,
  useComposerWindow,
  useDraftManagement,
  useEmailForm,
  useEmailProcessing,
} from '../hooks';
import {
  extractEmailAddress,
  formatSenderDisplay,
  isValidEmail,
} from '../utils/emailAddressUtils';
import {
  getMessageIdForSend,
  getReferences,
  getReplyToMsgId,
  normalizeMessageCategoryForAPI,
  shouldIncludeMessageCategory,
} from '../utils/emailTypeUtils';
import {
  cleanFormData,
  validateEmailAddresses,
  validateEmailForm,
} from '../utils/formValidationUtils';
import { ensureHtmlDocument } from '../utils/htmlContentUtils';
import {
  isSameSenderAddress,
  resolveSenderName,
} from '../utils/senderNameUtils';
import { SignatureManager } from '../utils/signatureManager';
import AIAssistPanel from './AIAssistPanel';
import { AttachmentChip } from './AttachmentChip';
import classes from './CmailComposer.module.css';

// Custom Email Chip Input Component
interface EmailChipInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label: string;
  error?: boolean;
  helperText?: string;
}

const EmailChipInput: React.FC<EmailChipInputProps> = ({
  value,
  onChange,
  placeholder,
  label,
  error,
  helperText,
}) => {
  const emails = React.useMemo(() => {
    if (!value.trim()) return [];
    return value
      .split(',')
      .map((email) => email.trim())
      .filter((email) => email);
  }, [value]);

  const handleTagsChange = (newTags: string[]) => {
    const validTags = newTags.filter((tag) => isValidEmail(tag.trim()));
    onChange(validTags.join(', '));
  };

  return (
    <Box className={classes.emailChipInputWrapper}>
      <TagsInput
        value={emails}
        onChange={handleTagsChange}
        placeholder={placeholder}
        splitChars={[',', ' ']}
        error={error ? helperText : undefined}
        variant="unstyled"
        size="sm"
        classNames={{
          input: classes.tagsInputInput,
          pill: classes.tagsInputPill,
        }}
      />
    </Box>
  );
};

const sentMsgIdFrom = (response: unknown): string | undefined => {
  const msgId = (response as { msgId?: unknown } | null | undefined)?.msgId;
  return typeof msgId === 'string' ? msgId : undefined;
};

interface CmailComposerProps {
  open: boolean;
  onClose: () => void;
  mode?: 'new' | 'reply' | 'reply-single' | 'forward';
  message?: MessageInfo;
  msgCategory?: string;
  missingFields?: Record<string, any>;
  missingFieldsDescription?: string;
  currentDraftMsgId?: string | null;
  /**
   * Called once after a send succeeds, before `onClose`, with what went out. Not called on close,
   * delete or a failed send. `msgId` is whatever id the send response carries; the endpoint
   * currently answers with plain text, so it is undefined today.
   */
  onSent?: (sent: { msgId?: string; to: string; cc: string[] }) => void;
  /**
   * Where a reply to the email should go, sent as its `Reply-To` header. Absent, the send carries none and the
   * reply goes to the sender, as before.
   */
  replyTo?: readonly string[];
}

export default function CmailComposer({
  open,
  onClose,
  mode = 'new',
  message,
  msgCategory,
  missingFields,
  missingFieldsDescription,
  currentDraftMsgId: propCurrentDraftMsgId,
  onSent,
  replyTo,
}: CmailComposerProps) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isMinimized, setIsMinimized] = React.useState(false);
  // Track if user manually toggled CC/BCC visibility
  // Initialize based on whether draft has CC/BCC recipients
  const [userToggledCcBcc, setUserToggledCcBcc] = React.useState(() => {
    if (message && message.emailDirection === 'DRAFT') {
      if (message.receivers && message.receivers.length > 0) {
        const hasCcOrBcc = message.receivers.some(
          (receiver: MessageInfoReceiver) =>
            receiver.type === 'CC' || receiver.type === 'BCC'
        );
        return hasCcOrBcc;
      }
      const hasCcOrBcc = !!(
        message.headers?.Cc ||
        message.headers?.cc ||
        message.headers?.CC ||
        message.headers?.Bcc ||
        message.headers?.bcc ||
        message.headers?.BCC
      );
      return hasCcOrBcc;
    }
    return false;
  });

  const [userToggledBcc, setUserToggledBcc] = React.useState(() => {
    if (message && message.emailDirection === 'DRAFT') {
      if (message.receivers && message.receivers.length > 0) {
        const hasBcc = message.receivers.some(
          (receiver: MessageInfoReceiver) => receiver.type === 'BCC'
        );
        return hasBcc;
      }
      const hasBcc = !!(
        message.headers?.Bcc ||
        message.headers?.bcc ||
        message.headers?.BCC
      );
      return hasBcc;
    }
    return false;
  });
  const [isRecipientsExpanded, setIsRecipientsExpanded] = React.useState(true);
  const [isInitializing, setIsInitializing] = React.useState(false);
  const [isSending, setIsSending] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const bodyRef = React.useRef<HTMLDivElement>(null);
  const { windowRef, startDrag } = useComposerWindow({
    open,
    isExpanded,
    isMinimized,
  });

  // Remember what was focused before opening so we can restore focus on close
  // (the removed Mantine Modal did this via its focus trap / returnFocus).
  const lastFocusedRef = React.useRef<HTMLElement | null>(null);
  React.useEffect(() => {
    if (open) lastFocusedRef.current = document.activeElement as HTMLElement;
  }, [open]);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [selectedFromEmail, setSelectedFromEmail] = React.useState('');
  const handleAPIError = useAPIErrorHandler();
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  // Select the user from the Redux store
  const user = useSelector((state: any) => state.user);
  const userEmail = user?.email || 'loading...';

  const [currentDraftMsgId, setCurrentDraftMsgId] = React.useState<
    string | null
  >(propCurrentDraftMsgId || null);
  const [hasAutoTriggeredAI, setHasAutoTriggeredAI] = React.useState(false);

  // Sync currentDraftMsgId with prop when parent owns the draft id (e.g. submissions modal).
  // Do not push null/undefined from parent — that would clear a draft id after save.
  React.useEffect(() => {
    if (propCurrentDraftMsgId == null) {
      return;
    }
    if (propCurrentDraftMsgId !== currentDraftMsgId) {
      setCurrentDraftMsgId(propCurrentDraftMsgId);
    }
  }, [propCurrentDraftMsgId, currentDraftMsgId]);

  // Permission checks for delete functionality
  const hasRootPermission = useAllow(['root:root']);
  const isDraftMessage = message?.emailDirection === 'DRAFT';
  const canDelete = hasRootPermission || isDraftMessage;

  // Signature hooks
  const systemSignaturesHook = useSystemSignatures();
  const systemSignatures = systemSignaturesHook.systemSignatures;

  // Get team senders from system signatures - purely API-driven
  const teamSenders = React.useMemo(() => {
    if (!systemSignatures || systemSignatures.length === 0) {
      return [];
    }

    const uniqueSenders = new Map<
      string,
      {
        email: string;
        firstName: string;
        lastName: string;
        displayName: string;
      }
    >();

    systemSignatures.forEach((signature) => {
      const key = signature.email;
      if (!uniqueSenders.has(key)) {
        uniqueSenders.set(key, {
          email: signature.email,
          firstName: signature.firstName,
          lastName: signature.lastName,
          displayName: `${signature.firstName} ${signature.lastName}`.trim(),
        });
      }
    });

    const senders = Array.from(uniqueSenders.values());

    return senders;
  }, [systemSignatures]);

  const [fromEmailSignatures, setFromEmailSignatures] = React.useState<
    SignaturesByEmailResponse['signatures']
  >([]);
  const [fromEmailUserInfo, setFromEmailUserInfo] = React.useState<
    SignaturesByEmailResponse['userInfo'] | null
  >(null);

  React.useEffect(() => {
    let isCancelled = false;

    const getNormalizedEmail = (email: string) => email.trim().toLowerCase();
    const normalizedFromEmail = extractEmailAddress(selectedFromEmail);

    const getPersonalSignaturesForFromEmail = async () => {
      const response = await signaturesApi.getUserSignatures();
      const rawData = response.data;
      const signatureRows = Array.isArray(rawData)
        ? rawData
        : ((rawData as any)?.content as any[]) || [];

      const matchingSignatures = signatureRows.filter((signature: any) => {
        const sigEmail = signature?.email;
        return (
          sigEmail &&
          getNormalizedEmail(sigEmail) ===
            getNormalizedEmail(normalizedFromEmail)
        );
      });

      return matchingSignatures.map((signature: any) => ({
        id: signature.id,
        name: signature.name,
        content: signature.content,
        isDefault: Boolean(signature.isDefault),
        isActive: true,
      }));
    };

    async function loadSignaturesForFromEmail() {
      if (!open) {
        return;
      }

      if (!selectedFromEmail || selectedFromEmail === 'loading...') {
        setFromEmailSignatures([]);
        setFromEmailUserInfo(null);
        return;
      }

      try {
        const response = await getSignaturesByEmail(normalizedFromEmail);
        if (isCancelled) return;

        const responseData = response.data;
        const responseSignatures = Array.isArray(responseData)
          ? responseData
          : responseData?.signatures || [];

        if (responseSignatures.length > 0) {
          const normalizedSignatures = responseSignatures.map((sig: any) => ({
            id: sig.id,
            name: sig.name,
            content: sig.content,
            isDefault: Boolean(sig.isDefault),
            isActive: true,
          }));
          setFromEmailSignatures(normalizedSignatures);

          const userInfo =
            responseData?.userInfo ||
            (responseSignatures[0]
              ? {
                  email: responseSignatures[0].email || normalizedFromEmail,
                  firstName: responseSignatures[0].firstName || '',
                  lastName: responseSignatures[0].lastName || '',
                  type: 'user' as const,
                }
              : null);
          setFromEmailUserInfo(userInfo);
          return;
        }

        // No by-email signatures, try personal signatures
        const personalSignatures = await getPersonalSignaturesForFromEmail();
        if (isCancelled) return;

        if (personalSignatures.length > 0) {
          setFromEmailSignatures(personalSignatures as any);
          setFromEmailUserInfo({
            email: normalizedFromEmail,
            firstName: user?.firstName || '',
            lastName: user?.lastName || '',
            type: 'user',
          });
          return;
        }

        // Fall back to system signatures
        const fallbackSystemSignatures = systemSignatures.filter(
          (signature) =>
            getNormalizedEmail(signature.email) ===
            getNormalizedEmail(normalizedFromEmail)
        );
        setFromEmailSignatures(fallbackSystemSignatures as any);
        setFromEmailUserInfo(null);
      } catch {
        if (isCancelled) return;

        // Try personal signatures on error
        try {
          const personalSignatures = await getPersonalSignaturesForFromEmail();
          if (isCancelled) return;

          if (personalSignatures.length > 0) {
            setFromEmailSignatures(personalSignatures as any);
            setFromEmailUserInfo({
              email: normalizedFromEmail,
              firstName: user?.firstName || '',
              lastName: user?.lastName || '',
              type: 'user',
            });
            return;
          }
        } catch {
          // Ignore and proceed to system fallback
        }

        // System fallback after all errors
        const fallbackSystemSignatures = systemSignatures.filter(
          (signature) =>
            getNormalizedEmail(signature.email) ===
            getNormalizedEmail(normalizedFromEmail)
        );
        setFromEmailSignatures(fallbackSystemSignatures as any);
        setFromEmailUserInfo(null);
      }
    }

    loadSignaturesForFromEmail();

    return () => {
      isCancelled = true;
    };
  }, [
    open,
    selectedFromEmail,
    systemSignatures,
    user?.firstName,
    user?.lastName,
  ]);

  // Get available signatures based on selected from email
  const availableSignatures = React.useMemo(
    () => fromEmailSignatures || [],
    [fromEmailSignatures]
  );
  const [selectedSignatureId, setSelectedSignatureId] = React.useState<
    string | null
  >(null);
  const [isManualSignatureChange, setIsManualSignatureChange] =
    React.useState(false);

  /**
   * The name that goes beside the From address, for the select's own label, for the send payload
   * and for every draft saved along the way. One answer: a draft that was opened as one sender
   * and switched to another used to keep the first name on screen while sending under the second.
   */
  const getSenderName = React.useCallback(
    () =>
      resolveSenderName({
        selectedFromEmail,
        selectedSignatureId,
        teamSenders,
        systemSignatures,
        fromEmailUserInfo,
        user,
        draft: message?.emailDirection === 'DRAFT' ? message : null,
      }),
    [
      selectedFromEmail,
      selectedSignatureId,
      teamSenders,
      systemSignatures,
      fromEmailUserInfo,
      user,
      message,
    ]
  );

  // Initialize hooks
  const emailFormHook = useEmailForm({
    message: message,
    mode,
    userEmail,
    user,
    isInitializing,
    setIsInitializing,
  });

  const emailProcessingHook = useEmailProcessing({
    message: message,
    mode,
    setCurrentDraftMsgId,
  });

  const attachmentHook = useAttachmentManagement({
    form: emailFormHook.form,
    setForm: emailFormHook.setForm,
    currentDraftMsgId,
    setCurrentDraftMsgId,
    selectedFromEmail,
    getSenderName,
    msgCategory,
    message,
    mode,
    getEmailType: emailProcessingHook.getEmailType,
    getReferences: emailProcessingHook.getReferences,
    cleanFormData: emailFormHook.cleanFormData,
  });

  // Once CC/BCC fields have values, mark them as "toggled" so they persist even if cleared
  React.useEffect(() => {
    if (
      (emailFormHook.form.cc?.trim() || emailFormHook.form.bcc?.trim()) &&
      !userToggledCcBcc
    ) {
      setUserToggledCcBcc(true);
    }
  }, [emailFormHook.form.cc, emailFormHook.form.bcc, userToggledCcBcc]);

  React.useEffect(() => {
    if (emailFormHook.form.bcc?.trim() && !userToggledBcc) {
      setUserToggledBcc(true);
    }
  }, [emailFormHook.form.bcc, userToggledBcc]);

  // Derived state: Show CC/BCC if user toggled them OR if they have values
  const showCcBcc =
    userToggledCcBcc ||
    !!(emailFormHook.form.cc?.trim() || emailFormHook.form.bcc?.trim());
  const showBcc = userToggledBcc || !!emailFormHook.form.bcc?.trim();

  const aiAssistHook = useAIAssist({
    form: emailFormHook.form,
    setForm: emailFormHook.setForm,
    msgCategory,
    getEmailType: emailProcessingHook.getEmailType,
    missingFields,
    missingFieldsDescription,
  });

  // Derived right after the hook so effects below can list it as a dependency
  // without a temporal-dead-zone ReferenceError (was previously declared ~250 lines down).
  const isAiPanelOpen = aiAssistHook.aiAssistOpen;

  const draftHook = useDraftManagement({
    form: emailFormHook.form,
    message,
    mode,
    currentDraftMsgId,
    setCurrentDraftMsgId,
    selectedFromEmail,
    getSenderName,
    msgCategory,
    getEmailType: emailProcessingHook.getEmailType,
    getReferences: emailProcessingHook.getReferences,
    cleanFormData: emailFormHook.cleanFormData,
    hasUnsavedChanges: emailFormHook.hasUnsavedChanges,
  });

  // Track which message we already created a forward draft for (avoid duplicate drafts).
  // Only used when mode === 'forward'; other flows (new, reply, existing draft) are unchanged.
  const forwardDraftCreatedForMsgIdRef = React.useRef<string | null>(null);

  // Create forward draft when composer opens in forward mode so attachment upload has the correct msgId.
  // Other flows: new/reply create draft on first attachment; opening an existing draft already has currentDraftMsgId.
  React.useEffect(() => {
    if (!open) {
      forwardDraftCreatedForMsgIdRef.current = null;
      return;
    }
    if (
      mode !== 'forward' ||
      !message?.msgId ||
      currentDraftMsgId != null ||
      isInitializing ||
      forwardDraftCreatedForMsgIdRef.current === message.msgId
    ) {
      return;
    }
    forwardDraftCreatedForMsgIdRef.current = message.msgId;
    draftHook.saveDraftMessage(emailFormHook.form);
  }, [open, mode, message?.msgId, currentDraftMsgId, isInitializing]);

  // Get signature content helper
  const getSignatureContent = (signatureId: string) => {
    return fromEmailSignatures.find((signature) => signature.id === signatureId)
      ?.content;
  };

  // Auto-select default signature when From email changes
  React.useEffect(() => {
    if (!open) return;

    if (isManualSignatureChange) {
      return;
    }

    if (!selectedFromEmail || selectedFromEmail === '') {
      return;
    }

    const isDraft = message && message.emailDirection === 'DRAFT';
    if (isDraft) {
      const currentContent = emailFormHook.form.body || '';
      const existingSignatureId =
        SignatureManager.getSignatureId(currentContent);

      if (existingSignatureId) {
        setSelectedSignatureId(existingSignatureId);
      } else {
        setSelectedSignatureId(null);
      }
      return;
    }

    const defaultSignature = availableSignatures.find((sig) => sig.isDefault);
    const targetSignature = defaultSignature || availableSignatures[0];

    if (targetSignature && targetSignature.id !== selectedSignatureId) {
      setSelectedSignatureId(targetSignature.id);

      const currentContent = emailFormHook.form.body || '';
      const emailType = emailProcessingHook.getEmailType() as
        | 'NEW'
        | 'REPLY'
        | 'FORWARD'
        | 'FOLLOWUP';

      const signature = {
        id: targetSignature.id,
        name: targetSignature.name,
        content: getSignatureContent(targetSignature.id) || '',
        isDefault: targetSignature.isDefault,
      };

      let newContent;
      const existingSignatureId =
        SignatureManager.getSignatureId(currentContent);

      if (existingSignatureId) {
        newContent = SignatureManager.replaceSignature(
          currentContent,
          signature,
          emailType
        );
      } else if (
        SignatureManager.isSafeToInsertSignature(currentContent, emailType)
      ) {
        newContent = SignatureManager.insertSignature(
          currentContent,
          signature,
          emailType
        );
      } else {
        newContent = SignatureManager.insertSignature(
          currentContent,
          signature,
          emailType
        );
      }

      if (newContent !== currentContent) {
        emailFormHook.setForm((prev) => ({ ...prev, body: newContent }));
      }
    } else if (!targetSignature && selectedSignatureId) {
      setSelectedSignatureId(null);
      const currentContent = emailFormHook.form.body || '';
      const newContent = SignatureManager.removeSignature(currentContent);
      if (newContent !== currentContent) {
        emailFormHook.setForm((prev) => ({ ...prev, body: newContent }));
      }
    }
  }, [selectedFromEmail, availableSignatures, open, isManualSignatureChange]);

  // Reset manual signature change flag when from email changes
  React.useEffect(() => {
    setIsManualSignatureChange(false);
  }, [selectedFromEmail]);

  // Handle manual signature changes
  const handleSignatureChange = (signatureId: string | null) => {
    const newSignatureId =
      signatureId === '' || !signatureId ? null : signatureId;
    setSelectedSignatureId(newSignatureId);
    setIsManualSignatureChange(true);

    const currentContent = emailFormHook.form.body || '';
    const emailType = emailProcessingHook.getEmailType() as
      | 'NEW'
      | 'REPLY'
      | 'FORWARD'
      | 'FOLLOWUP';
    let newContent;

    if (newSignatureId === null) {
      newContent = SignatureManager.removeSignature(currentContent);
    } else {
      const signatureContent = getSignatureContent(newSignatureId);
      if (signatureContent) {
        const signature = {
          id: newSignatureId,
          name:
            availableSignatures.find((sig) => sig.id === newSignatureId)
              ?.name || '',
          content: signatureContent,
          isDefault: false,
        };

        const existingSignatureId =
          SignatureManager.getSignatureId(currentContent);

        if (existingSignatureId) {
          newContent = SignatureManager.replaceSignature(
            currentContent,
            signature,
            emailType
          );
        } else {
          newContent = SignatureManager.insertSignature(
            currentContent,
            signature,
            emailType
          );
        }
      } else {
        newContent = currentContent;
      }
    }

    if (newContent !== currentContent) {
      emailFormHook.setForm((prev) => ({ ...prev, body: newContent }));
    }
  };

  // Set DOM content when form is initialized or when the form body changes.
  // The body div is always mounted (hidden via display:none when minimized),
  // so bodyRef is always available here — no setTimeout fallback is needed.
  React.useEffect(() => {
    if (bodyRef.current && open && emailFormHook.form.body) {
      if (emailFormHook.form.body !== bodyRef.current.innerHTML) {
        bodyRef.current.innerHTML = emailFormHook.form.body;
      }
    }
  }, [emailFormHook.form.body, open, isInitializing]);

  // Handle body content changes with better draft support
  const handleBodyChange = (event: React.FormEvent<HTMLDivElement>) => {
    if (isInitializing) {
      return;
    }

    const content = event.currentTarget.innerHTML;

    const isDraft = message && message.emailDirection === 'DRAFT';
    if (isDraft && !content.trim() && emailFormHook.form.body.trim()) {
      return;
    }

    emailFormHook.setForm((prev) => ({ ...prev, body: content }));
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault();
    const plainText = event.clipboardData.getData('text/plain');
    const htmlSafe = plainText
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\n/g, '<br>');
    document.execCommand('insertHTML', false, htmlSafe);
  };

  const handleFormat = (command: string) => {
    document.execCommand(command, false);
    if (bodyRef.current) {
      emailFormHook.setForm((prev) => ({
        ...prev,
        body: bodyRef.current?.innerHTML || '',
      }));
    }
  };

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const success = await draftHook.handleDelete();
      if (success) {
        setIsExpanded(false);
        setIsMinimized(false);
        aiAssistHook.handleAIModalClose();
        onClose();
      }
    } finally {
      setIsDeleting(false);
    }
  };

  // Custom close handler that saves draft and calls parent onClose
  const handleClose = async () => {
    await draftHook.handleClose();

    setIsExpanded(false);
    setIsMinimized(false);
    aiAssistHook.handleAIModalClose();
    onClose();
    lastFocusedRef.current?.focus?.({ preventScroll: true });
  };

  const handleToggleMinimize = () => {
    setIsMinimized((prev) => {
      const next = !prev;
      if (next) {
        setIsExpanded(false);
        aiAssistHook.handleAIModalClose();
      }
      return next;
    });
  };

  const handleToggleExpand = () => {
    setIsMinimized(false);
    setIsExpanded((prev) => !prev);
  };

  // Close on Escape only in maximized (modal-like) mode; the floating window is
  // non-blocking, so Escape there should not discard an in-progress draft.
  React.useEffect(() => {
    if (!open || !isExpanded) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, isExpanded, handleClose]);

  const recipientRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        recipientRef.current &&
        !recipientRef.current.contains(event.target as Node) &&
        isRecipientsExpanded
      ) {
        setIsRecipientsExpanded(false);
      }
    };

    if (isRecipientsExpanded) {
      document.addEventListener('mouseup', handleClickOutside);
    } else {
      document.removeEventListener('mouseup', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mouseup', handleClickOutside);
    };
  }, [isRecipientsExpanded]);

  // Initialize selectedFromEmail when userEmail is available
  React.useEffect(() => {
    if (userEmail && userEmail !== 'loading...') {
      const isDraft = message && message.emailDirection === 'DRAFT';
      if (isDraft && message?.senderEmail) {
        setSelectedFromEmail(message.senderEmail);
      } else {
        setSelectedFromEmail(userEmail);
      }
    }
  }, [userEmail, message]);

  // Auto-trigger AI Assist when composer opens with missing fields data
  React.useEffect(() => {
    if (
      open &&
      missingFields &&
      Object.keys(missingFields).length > 0 &&
      !isInitializing &&
      !hasAutoTriggeredAI &&
      !isDraftMessage
    ) {
      const timer = setTimeout(() => {
        aiAssistHook.handleAIAssist();
        setHasAutoTriggeredAI(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [
    open,
    missingFields,
    isInitializing,
    aiAssistHook.handleAIAssist,
    hasAutoTriggeredAI,
    isDraftMessage,
  ]);

  // Reset auto-trigger flag when composer closes
  React.useEffect(() => {
    if (!open) {
      setHasAutoTriggeredAI(false);
    }
  }, [open]);

  const getHeaderTitle = () => {
    switch (mode) {
      case 'reply':
        return 'Reply All';
      case 'reply-single':
        return 'Reply';
      case 'forward':
        return 'Forward';
      default:
        return 'New Message';
    }
  };

  const handleSend = async () => {
    const validation = validateEmailForm(emailFormHook.form, mode);
    if (!validation.isValid) {
      notifications.error(validation.errors[0]);
      return;
    }

    if (validation.warnings.length > 0) {
      validation.warnings.forEach((warning) => {
        notifications.warning(warning);
      });
    }

    setIsSending(true);
    try {
      const emailType = emailProcessingHook.getEmailType() as
        | 'NEW'
        | 'REPLY'
        | 'FORWARD'
        | 'FOLLOWUP';
      const references = getReferences(message, mode);
      const cleanedForm = cleanFormData(emailFormHook.form);

      if (!cleanedForm.to) {
        throw new Error('Recipient (To) is required');
      }

      const shouldIncludeMsgCategoryForSend =
        shouldIncludeMessageCategory(msgCategory);

      const emailPayload = {
        to: cleanedForm.to,
        cc: cleanedForm.cc,
        bcc: cleanedForm.bcc,
        subject: cleanedForm.subject,
        body: cleanedForm.body,
        htmlBody: ensureHtmlDocument(cleanedForm.body),
        attachments: cleanedForm.attachments.map((attachment) => ({
          docId: attachment.docId,
          docName: attachment.docName,
          docContentType: attachment.docContentType,
          docSize: attachment.docSize,
        })),
        from: selectedFromEmail,
        senderName: getSenderName(),
        msgId: getMessageIdForSend(message, currentDraftMsgId),
        references: references.length > 0 ? references : undefined,
        replyToMsgId: getReplyToMsgId(message, mode),
        emailType: emailType as 'NEW' | 'REPLY' | 'FORWARD' | 'FOLLOWUP',
        ...(shouldIncludeMsgCategoryForSend && {
          msgCategory: normalizeMessageCategoryForAPI(msgCategory),
        }),
        ...(replyTo && replyTo.length > 0 && { replyTo: [...replyTo] }),
      };

      const sendResponse: unknown = await sendEmail({
        data: emailPayload,
      });
      notifications.success('Email sent successfully');
      // The mail is already out, so a throwing caller must not read as a failed send: the
      // composer would stay open and invite a second send.
      try {
        onSent?.({
          msgId: sentMsgIdFrom(sendResponse),
          to: emailPayload.to,
          cc: (emailPayload.cc ?? '')
            .split(',')
            .map((address) => address.trim())
            .filter(Boolean),
        });
      } catch (onSentError) {
        console.error('CmailComposer onSent callback failed', onSentError);
      }

      queryClient.invalidateQueries(queries.cmail.list._def);
      queryClient.invalidateQueries(['draft-stats']);

      setIsExpanded(false);
      setIsMinimized(false);
      aiAssistHook.handleAIModalClose();
      onClose();
    } catch (error: any) {
      handleAPIError('Failed to send email')(error);
    } finally {
      setIsSending(false);
    }
  };

  // Build "From" select data
  const fromSelectData = React.useMemo(() => {
    /*
     * The selected address is labelled by the resolver -- it is the one whose name has to follow
     * a signature change -- and every other address by whoever owns it. An address with no name
     * to show is labelled by itself rather than by "undefined undefined".
     */
    const optionLabel = (email: string, ownerName: string) => {
      const name = isSameSenderAddress(email, selectedFromEmail)
        ? getSenderName()
        : ownerName;
      return formatSenderDisplay(name, email);
    };

    const options = [
      {
        value: userEmail,
        label: optionLabel(
          userEmail,
          `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()
        ),
      },
    ];

    teamSenders.forEach((sender) => {
      options.push({
        value: sender.email,
        label: optionLabel(sender.email, sender.displayName),
      });
    });

    // Add draft sender if not already in list
    if (message && message.emailDirection === 'DRAFT' && message.senderEmail) {
      const draftEmail = extractEmailAddress(message.senderEmail);
      const alreadyExists =
        draftEmail === userEmail ||
        teamSenders.some((sender) => sender.email === draftEmail);

      if (!alreadyExists) {
        options.push({
          value: draftEmail,
          label: optionLabel(draftEmail, (message.senderName ?? '').trim()),
        });
      }
    }

    return options;
  }, [userEmail, user, teamSenders, selectedFromEmail, message, getSenderName]);

  // Build signature select data
  const signatureSelectData = React.useMemo(() => {
    const options = [{ value: '', label: 'No signature' }];
    availableSignatures.forEach((signature) => {
      options.push({
        value: signature.id,
        label: `${signature.name}${signature.isDefault ? ' (Default)' : ''}`,
      });
    });
    return options;
  }, [availableSignatures]);

  const renderSignatureDropdown = () => {
    if (availableSignatures.length === 0) return null;

    return (
      <Flex align="center" ml="auto">
        <Text size="sm" mr="xs" className={classes.signatureLabel}>
          Signature
        </Text>
        <Select
          value={selectedSignatureId || ''}
          onChange={handleSignatureChange}
          data={signatureSelectData}
          size="xs"
          variant="unstyled"
          className={classes.signatureSelectMinWidth}
          comboboxProps={{ zIndex: 10000 }}
        />
      </Flex>
    );
  };

  if (!open) return null;

  const windowClassName = [
    classes.window,
    isExpanded ? classes.windowMaximized : '',
    isMinimized ? classes.windowMinimized : '',
    isAiPanelOpen ? classes.windowAiOpen : '',
  ]
    .filter(Boolean)
    .join(' ');

  return createPortal(
    <>
      {/* Backdrop for maximized mode; click-outside closes */}
      {isExpanded && (
        <Box
          className={classes.overlay}
          onClick={handleClose}
          aria-hidden="true"
        />
      )}
      <div
        ref={windowRef}
        role="dialog"
        aria-modal={isExpanded}
        aria-label={getHeaderTitle()}
        className={windowClassName}
      >
        {/* Compose Panel (left) */}
        <Box
          className={classes.composePanel}
          style={
            {
              '--compose-panel-width': isAiPanelOpen ? '70%' : '100%',
            } as React.CSSProperties
          }
        >
          {/* Header */}
          <Flex
            justify="space-between"
            align="center"
            px="sm"
            py="xs"
            className={classes.headerBar}
            onPointerDown={startDrag}
          >
            <Flex
              align="center"
              gap="xs"
              className={isMinimized ? classes.headerTitleClickable : undefined}
              onClick={isMinimized ? () => setIsMinimized(false) : undefined}
            >
              {mode !== 'new' && (
                <FontAwesomeIcon
                  icon={faReply}
                  fontSize="0.875rem"
                  className={classes.replyIcon}
                  style={
                    {
                      '--reply-icon-transform':
                        mode === 'forward' ? 'scaleX(-1)' : 'none',
                    } as any
                  }
                />
              )}
              <Text size="sm" fw={600}>
                {getHeaderTitle()}
              </Text>
            </Flex>
            <Flex align="center" gap="xs">
              <ActionIcon
                variant="subtle"
                size="sm"
                onClick={handleToggleMinimize}
              >
                <FontAwesomeIcon icon={faMinus} fontSize="0.875rem" />
              </ActionIcon>
              <ActionIcon
                variant="subtle"
                size="sm"
                onClick={handleToggleExpand}
              >
                <FontAwesomeIcon
                  icon={faUpRightAndDownLeftFromCenter}
                  fontSize="0.875rem"
                />
              </ActionIcon>
              <ActionIcon variant="subtle" size="sm" onClick={handleClose}>
                <FontAwesomeIcon icon={faXmark} fontSize="0.875rem" />
              </ActionIcon>
            </Flex>
          </Flex>

          <Box
            className={classes.composeContent}
            style={
              {
                display: isMinimized ? 'none' : undefined,
              } as React.CSSProperties
            }
          >
            <>
              {/* Fields */}
              <Box className={classes.fieldsContainer}>
                {!isRecipientsExpanded ? (
                  <Flex
                    align="center"
                    justify="space-between"
                    className={classes.collapsedRecipientsRow}
                    py={4}
                    px={isAiPanelOpen ? 'xs' : 'sm'}
                  >
                    <Box
                      className={classes.recipientsClickArea}
                      onClick={() => setIsRecipientsExpanded(true)}
                      ref={recipientRef}
                    >
                      <Text
                        size="sm"
                        c={
                          emailFormHook.form.to || emailFormHook.form.cc
                            ? undefined
                            : 'dimmed'
                        }
                      >
                        {emailFormHook.form.to || emailFormHook.form.cc
                          ? [emailFormHook.form.to, emailFormHook.form.cc]
                              .filter(Boolean)
                              .join(', ')
                          : 'Recipients'}
                      </Text>
                    </Box>

                    {renderSignatureDropdown()}
                  </Flex>
                ) : (
                  <>
                    <Flex
                      align="center"
                      gap="xs"
                      w="100%"
                      px={isAiPanelOpen ? 'xs' : 'sm'}
                      pt="xs"
                    >
                      <Text size="sm" className={classes.fieldLabel}>
                        From
                      </Text>
                      <Select
                        aria-label="From"
                        value={selectedFromEmail}
                        onChange={(value) => {
                          if (value) setSelectedFromEmail(value);
                        }}
                        data={fromSelectData}
                        size="xs"
                        variant="unstyled"
                        className={classes.fromSelect}
                        comboboxProps={{ zIndex: 10000 }}
                      />

                      {renderSignatureDropdown()}
                    </Flex>
                    <Box
                      className={classes.recipientsColumn}
                      px={isAiPanelOpen ? 'xs' : 'sm'}
                    >
                      <Flex align="center" className={classes.fieldRow}>
                        <Text size="sm" mr="xs" className={classes.fieldLabel}>
                          To
                        </Text>
                        <EmailChipInput
                          value={emailFormHook.form.to}
                          onChange={(value) => {
                            emailFormHook.setForm((prev) => ({
                              ...prev,
                              to: value,
                            }));
                          }}
                          placeholder="Add recipients"
                          label="To"
                          error={
                            !!emailFormHook.form.to &&
                            !validateEmailAddresses(emailFormHook.form.to).valid
                              .length
                          }
                          helperText={
                            emailFormHook.form.to &&
                            !validateEmailAddresses(emailFormHook.form.to).valid
                              .length
                              ? 'Invalid email address'
                              : ''
                          }
                        />
                        {!showCcBcc && (
                          <Button
                            variant="subtle"
                            size="compact-sm"
                            onClick={() => setUserToggledCcBcc(true)}
                            ml="xs"
                            px="xs"
                          >
                            Cc
                          </Button>
                        )}
                        {!showBcc && (
                          <Button
                            variant="subtle"
                            size="compact-sm"
                            onClick={() => setUserToggledBcc(true)}
                            ml="xs"
                            px="xs"
                          >
                            Bcc
                          </Button>
                        )}
                      </Flex>
                      {showCcBcc && (
                        <>
                          <Divider my={4} opacity={0.3} />
                          <Flex align="center" gap="sm" w="100%">
                            <Flex align="center" className={classes.fieldGrow}>
                              <Text
                                size="sm"
                                mr="xs"
                                className={classes.fieldLabel}
                              >
                                Cc
                              </Text>
                              <EmailChipInput
                                value={emailFormHook.form.cc}
                                onChange={(value) => {
                                  emailFormHook.setForm((prev) => ({
                                    ...prev,
                                    cc: value,
                                  }));
                                }}
                                placeholder="Add recipients"
                                label="Cc"
                                error={
                                  !!emailFormHook.form.cc &&
                                  !validateEmailAddresses(emailFormHook.form.cc)
                                    .valid.length
                                }
                                helperText={
                                  emailFormHook.form.cc &&
                                  !validateEmailAddresses(emailFormHook.form.cc)
                                    .valid.length
                                    ? 'Invalid email address'
                                    : ''
                                }
                              />
                            </Flex>
                          </Flex>
                        </>
                      )}
                      {showBcc && (
                        <>
                          <Divider my={4} opacity={0.3} />
                          <Flex align="center" gap="sm" w="100%">
                            <Flex align="center" className={classes.fieldGrow}>
                              <Text
                                size="sm"
                                mr="xs"
                                className={classes.fieldLabel}
                              >
                                Bcc
                              </Text>
                              <EmailChipInput
                                value={emailFormHook.form.bcc}
                                onChange={(value) => {
                                  emailFormHook.setForm((prev) => ({
                                    ...prev,
                                    bcc: value,
                                  }));
                                }}
                                placeholder="Add recipients"
                                label="Bcc"
                                error={
                                  !!emailFormHook.form.bcc &&
                                  !validateEmailAddresses(
                                    emailFormHook.form.bcc
                                  ).valid.length
                                }
                                helperText={
                                  emailFormHook.form.bcc &&
                                  !validateEmailAddresses(
                                    emailFormHook.form.bcc
                                  ).valid.length
                                    ? 'Invalid email address'
                                    : ''
                                }
                              />
                            </Flex>
                          </Flex>
                          <Divider my={4} opacity={0.3} />
                        </>
                      )}
                      {!showCcBcc && !showBcc && (
                        <Divider my={4} opacity={0.3} />
                      )}{' '}
                    </Box>
                  </>
                )}
                {/* Subject Field - Always visible */}
                <Divider my={6} opacity={0.5} />
                <Box px={isAiPanelOpen ? 'xs' : 'sm'} py={4}>
                  <TextInput
                    placeholder="Subject"
                    value={emailFormHook.form.subject}
                    onChange={emailFormHook.handleChange('subject')}
                    variant="unstyled"
                    className={classes.subjectInput}
                  />
                </Box>
                {/* Body editor */}
                <div
                  ref={bodyRef}
                  contentEditable
                  onInput={handleBodyChange}
                  onPaste={handlePaste}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      document.execCommand('insertLineBreak', false);
                    }
                  }}
                  suppressContentEditableWarning
                  className={classes.bodyEditor}
                />
                {/* Thin line separator above attachments */}
                {(attachmentHook.isUploadingAttachment ||
                  emailFormHook.form.attachments.length > 0) && (
                  <Box className={classes.attachmentSeparator} />
                )}
                {/* Attachments display */}
                {attachmentHook.isUploadingAttachment && (
                  <Flex
                    mt="xs"
                    mb={4}
                    align="center"
                    gap="xs"
                    c="dimmed"
                    px={isAiPanelOpen ? 'xs' : 'sm'}
                  >
                    <Text size="xs">Uploading attachments...</Text>
                  </Flex>
                )}
                {emailFormHook.form.attachments.length > 0 && (
                  <Box
                    className={classes.attachmentContainer}
                    px={isAiPanelOpen ? 'xs' : 'sm'}
                  >
                    <Flex wrap="wrap" gap="xs" pt="sm">
                      {emailFormHook.form.attachments.map((attachment) => (
                        <AttachmentChip
                          key={attachment.docId}
                          attachment={attachment}
                          onRemove={() =>
                            attachmentHook.handleRemoveAttachment(
                              attachment.docId
                            )
                          }
                        />
                      ))}
                    </Flex>
                  </Box>
                )}
              </Box>

              {/* Actions */}
              <Flex justify="space-between" align="center" px="sm" py="sm">
                <Flex align="center" gap={4}>
                  <Button
                    variant="outline"
                    size="sm"
                    leftSection={<FontAwesomeIcon icon={faWandMagicSparkles} />}
                    onClick={aiAssistHook.handleAIAssist}
                    radius="xl"
                    px="md"
                    fw={500}
                    color="var(--cmail-cowbell-blue)"
                    mr="xs"
                  >
                    Moo Assist
                  </Button>
                  <Button
                    variant="filled"
                    size="sm"
                    onClick={handleSend}
                    disabled={isSending}
                    radius="xl"
                    px="lg"
                    mr="xs"
                    fw={500}
                    color="var(--cmail-cowbell-blue)"
                  >
                    {isSending ? 'Sending...' : 'Send'}
                  </Button>
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    onClick={() => handleFormat('bold')}
                    c="dimmed"
                  >
                    <FontAwesomeIcon icon={faBold} fontSize="0.875rem" />
                  </ActionIcon>
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    onClick={() => handleFormat('italic')}
                    c="dimmed"
                  >
                    <FontAwesomeIcon icon={faItalic} fontSize="0.875rem" />
                  </ActionIcon>
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    onClick={handleAttachClick}
                    disabled={attachmentHook.isUploadingAttachment}
                    c="dimmed"
                  >
                    <FontAwesomeIcon icon={faPaperclip} fontSize="0.875rem" />
                  </ActionIcon>
                </Flex>

                {/* Trash can icon on the right */}
                {canDelete && (
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    onClick={handleDelete}
                    disabled={isDeleting}
                    c="dimmed"
                  >
                    <FontAwesomeIcon icon={faTrashCan} fontSize="0.875rem" />
                  </ActionIcon>
                )}
              </Flex>
            </>
          </Box>
        </Box>

        {/* AI Assist Panel */}
        <AIAssistPanel
          aiAssistOpen={aiAssistHook.aiAssistOpen}
          aiLoading={aiAssistHook.aiLoading}
          aiSuggestions={aiAssistHook.aiSuggestions}
          aiRefineMode={aiAssistHook.aiRefineMode}
          aiRefineInput={aiAssistHook.aiRefineInput}
          aiRefineTone={aiAssistHook.aiRefineTone}
          onClose={aiAssistHook.handleAIModalClose}
          onRefineModeToggle={() =>
            aiAssistHook.setAIRefineMode(!aiAssistHook.aiRefineMode)
          }
          onRefineInputChange={aiAssistHook.setAIRefineInput}
          onRefineToneChange={aiAssistHook.setAIRefineTone}
          onUseText={aiAssistHook.handleUseText}
          onRefine={aiAssistHook.handleRefine}
        />

        {/* Hidden file input element */}
        <input
          type="file"
          multiple
          ref={fileInputRef}
          onChange={attachmentHook.handleFileSelect}
          style={{ display: 'none' }}
        />
      </div>
    </>,
    document.body
  );
}
